import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Heart, 
  Shield, 
  Activity, 
  Apple, 
  Dumbbell,
  Award,
  Calendar,
  Clock,
  Save,
  Loader2
} from 'lucide-react';
import { IdentitySection } from './Passport/IdentitySection';
import { HealthSection } from './Passport/HealthSection';
import { NutritionSection } from './Passport/NutritionSection';
import { ExerciseSection } from './Passport/ExerciseSection';
import { TrainingSection } from './Passport/TrainingSection';
import { CalendarSection } from './Passport/CalendarSection';
import { DocumentsSection } from './Passport/DocumentsSection';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { PetPassportData } from '@/types/passport';

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
}

interface PetPassportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pet: Pet;
  onSuccess?: () => void;
}

const PetPassportModal = ({ open, onOpenChange, pet, onSuccess }: PetPassportModalProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passportData, setPassportData] = useState<PetPassportData | null>(null);
  const [activeTab, setActiveTab] = useState('identity');

  useEffect(() => {
    if (open && pet.id) {
      fetchPassportData();
    }
  }, [open, pet.id]);

  // Refetch when modal opens to ensure we have latest data
  useEffect(() => {
    if (open) {
      fetchPassportData();
    }
  }, [open]);

  const fetchPassportData = async () => {
    if (!pet.id) return;
    
    setLoading(true);
    try {
      // Use type assertion since pet_passports table might not be in types yet
      const { data, error } = await (supabase as any)
        .from('pet_passports')
        .select('*')
        .eq('pet_id', pet.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setPassportData(data as PetPassportData);
      } else {
        // Initialize empty passport without ID
        setPassportData({
          id: '', // We'll remove this when saving
          pet_id: pet.id,
          identity: {},
          public_profile: {},
          ownership: {},
          contacts: {},
          health: {},
          nutrition: {},
          exercise_wellbeing: {},
          genealogy: {},
          breeding: {},
          competitions: {},
          documents_media: {},
          reminders: {},
          privacy: {
            default_visibility: 'private',
            section_visibility: {
              identity: 'public',
              public_profile: 'public',
              ownership: 'private',
              contacts: 'private',
              health: 'private',
              nutrition: 'private',
              exercise: 'private',
              genealogy: 'private',
              breeding: 'private',
              competitions: 'public',
              documents_media: 'private'
            }
          },
          created_at: '',
          updated_at: ''
        });
      }
    } catch (error) {
      console.error('Error fetching passport data:', error);
      toast.error('Errore nel caricamento del passaporto');
    } finally {
      setLoading(false);
    }
  };

  const savePassportData = async () => {
    if (!passportData || !user) return;

    setSaving(true);
    try {
      const now = new Date().toISOString();
      
      if (passportData.id) {
        // Update existing passport
        const dataToUpdate = {
          ...passportData,
          pet_id: pet.id,
          user_id: user.id,
          updated_at: now
        };

        const { error } = await (supabase as any)
          .from('pet_passports')
          .update(dataToUpdate)
          .eq('id', passportData.id);

        if (error) throw error;
      } else {
        // Create new passport - don't include empty ID
        const { id, ...dataWithoutId } = passportData;
        const dataToInsert = {
          ...dataWithoutId,
          pet_id: pet.id,
          user_id: user.id,
          created_at: now,
          updated_at: now
        };

        const { data, error } = await (supabase as any)
          .from('pet_passports')
          .insert([dataToInsert])
          .select()
          .single();

        if (error) throw error;
        setPassportData(data as PetPassportData);
      }

      toast.success('Passaporto salvato con successo');
      onSuccess?.();
    } catch (error) {
      console.error('Error saving passport:', error);
      toast.error('Errore nel salvataggio del passaporto');
    } finally {
      setSaving(false);
    }
  };

  const updatePassportSection = (section: string, data: any) => {
    if (!passportData) return;
    
    setPassportData({
      ...passportData,
      [section]: {
        ...(passportData[section as keyof PetPassportData] as Record<string, any> || {}),
        ...data
      }
    });
  };

  const tabs = [
    { id: 'identity', label: 'Identità', icon: FileText, color: 'text-blue-500' },
    { id: 'health', label: 'Salute', icon: Heart, color: 'text-red-500' },
    { id: 'nutrition', label: 'Alimentazione', icon: Apple, color: 'text-green-500' },
    { id: 'exercise', label: 'Esercizio', icon: Dumbbell, color: 'text-orange-500' },
    { id: 'training', label: 'Addestramento', icon: Award, color: 'text-purple-500' },
    { id: 'calendar', label: 'Calendario', icon: Calendar, color: 'text-cyan-500' },
    { id: 'documents', label: 'Documenti', icon: Shield, color: 'text-indigo-500' }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:max-w-6xl h-[calc(100vh-1rem)] max-h-[calc(100vh-1rem)] p-0 overflow-hidden">
        <DialogHeader className="p-4 sm:p-6 pb-4 border-b border-white/10 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-xl sm:text-2xl font-bold text-white truncate">
                Passaporto di {pet.name}
              </DialogTitle>
              <p className="text-muted-foreground mt-1 text-sm">
                Gestisci tutte le informazioni del tuo {pet.species.toLowerCase()}
              </p>
            </div>
            <Button
              onClick={savePassportData}
              disabled={saving || loading}
              className="flex-shrink-0 w-full sm:w-auto"
              size="sm"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Salva
            </Button>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">Caricamento passaporto...</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row h-[calc(100vh-8rem)] overflow-hidden">
            {/* Mobile tabs - horizontal scroll */}
            <div className="sm:hidden border-b border-white/10 bg-black/20">
              <div className="w-full overflow-x-auto">
                <div className="flex p-2 space-x-2 min-w-max">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 p-2 rounded-lg transition-all whitespace-nowrap text-sm ${
                          activeTab === tab.id
                            ? 'bg-primary/20 text-white border border-primary/30'
                            : 'text-muted-foreground hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <Icon className={`w-4 h-4 ${tab.color}`} />
                        <span className="font-medium">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Desktop sidebar with tabs */}
            <div className="hidden sm:flex w-48 lg:w-64 border-r border-white/10 bg-black/20 flex-shrink-0">
              <ScrollArea className="h-full w-full">
                <div className="p-4 space-y-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                          activeTab === tab.id
                            ? 'bg-primary/20 text-white border border-primary/30'
                            : 'text-muted-foreground hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <Icon className={`w-5 h-5 ${tab.color}`} />
                        <span className="font-medium text-sm lg:text-base">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-4 sm:p-6">
                  {passportData && (
                    <>
                      {activeTab === 'identity' && (
                        <IdentitySection
                          data={passportData.identity || {}}
                          pet={pet}
                          onChange={(data) => updatePassportSection('identity', data)}
                        />
                      )}
                      {activeTab === 'health' && (
                        <HealthSection
                          data={passportData.health || {}}
                          onChange={(data) => updatePassportSection('health', data)}
                        />
                      )}
                      {activeTab === 'nutrition' && (
                        <NutritionSection
                          data={passportData.nutrition || {}}
                          onChange={(data) => updatePassportSection('nutrition', data)}
                        />
                      )}
                      {activeTab === 'exercise' && (
                        <ExerciseSection
                          data={passportData.exercise_wellbeing || {}}
                          onChange={(data) => updatePassportSection('exercise_wellbeing', data)}
                        />
                      )}
                      {activeTab === 'training' && (
                        <TrainingSection
                          data={passportData.competitions || {}}
                          onChange={(data) => updatePassportSection('competitions', data)}
                        />
                      )}
                      {activeTab === 'calendar' && (
                        <CalendarSection
                          data={passportData}
                          onChange={(data) => setPassportData(data as PetPassportData)}
                        />
                      )}
                      {activeTab === 'documents' && (
                        <DocumentsSection
                          data={passportData.documents_media || {}}
                          onChange={(data) => updatePassportSection('documents_media', data)}
                        />
                      )}
                    </>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PetPassportModal;